import React, { useRef, useEffect, useState } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { motion } from 'framer-motion';
import { createPublicApi } from '../../services/api';

const ReceiptGenerator = ({ order, onClose, slug }) => {
  const receiptRef = useRef();
  const [settings, setSettings] = useState({
    cafe_name: 'SwadZone',
    cafe_address: '42, MG Road, Koramangala, Bengaluru 560034',
    cafe_phone: '+91 98765 43210',
    cafe_email: 'hello@aromacafe.in',
    gst_enabled: 'false',
    gst_rate: '0',
    gst_number: '',
    gst_label: 'GST',
    navbar_logo_url: '',
    delivery_charge: '0',
  });

  useEffect(() => {
    createPublicApi(slug).settings.get()
      .then(r => { if (r.data.data) setSettings(prev => ({ ...prev, ...r.data.data })); })
      .catch(() => {});
  }, []);

  const items = order.items || [];
  const itemsTotal   = items.reduce((sum, i) => sum + parseFloat(i.subtotal || (i.price * i.quantity)), 0);
  const discountAmt  = parseFloat(order?.discount_amount) || 0;
  const couponCode   = order?.coupon_code || null;
  const afterDiscount = Math.max(itemsTotal - discountAmt, 0);
  const gstEnabled   = settings.gst_enabled === 'true' && parseFloat(settings.gst_rate) > 0;
  const gstRate      = parseFloat(settings.gst_rate) || 0;
  const gstAmount    = gstEnabled ? (afterDiscount * gstRate) / 100 : 0;
  const deliveryFee  = order?.order_type === 'delivery' ? (parseFloat(settings.delivery_charge) || 0) : 0;
  const grandTotal   = afterDiscount + gstAmount + deliveryFee;
  const actualTotal  = order?.total_amount ? parseFloat(order.total_amount) : grandTotal;
  const subtotal     = afterDiscount;

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true
    });
  };

  const handleDownloadPDF = async () => {
    const element = receiptRef.current;
    const canvas = await html2canvas(element, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [100, 280] });
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
    pdf.save(`receipt-${order.order_number || order.id}.pdf`);
  };

  const handlePrint = () => {
    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Receipt - Order #${order.order_number || order.id}</title>
          <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=DM+Sans:wght@400;500;600&display=swap" rel="stylesheet">
          <style>
            * { margin:0; padding:0; box-sizing:border-box; }
            body { font-family:'DM Sans',sans-serif; background:#fff; color:#1a0f00; }
            .receipt { width:380px; margin:0 auto; padding:32px 28px; }
            .center { text-align:center; }
            .logo { width:52px; height:52px; background:#c96428; border-radius:14px; display:inline-flex; align-items:center; justify-content:center; color:white; font-family:'Playfair Display',serif; font-size:24px; font-weight:700; margin-bottom:10px; }
            .cafe-name { font-family:'Playfair Display',serif; font-size:22px; font-weight:700; }
            .cafe-name span { color:#c96428; }
            .small { font-size:11px; color:#8b6347; line-height:1.6; margin-top:4px; }
            .tag { font-size:10px; font-weight:700; letter-spacing:3px; text-transform:uppercase; color:#c96428; margin-top:10px; }
            hr { border:none; border-top:2px dashed #e2d5c8; margin:18px 0; }
            .info-box { background:#fdf6f0; border-radius:10px; padding:14px 16px; margin-bottom:18px; }
            .row { display:flex; justify-content:space-between; font-size:12px; margin-bottom:5px; }
            .row:last-child { margin-bottom:0; }
            .lbl { color:#8b6347; }
            .val { font-weight:600; color:#1a0f00; }
            .order-id { color:#c96428 !important; font-size:15px !important; font-weight:700 !important; }
            .items-hdr { display:flex; justify-content:space-between; font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:1px; color:#8b6347; padding-bottom:8px; border-bottom:1px solid #e2d5c8; margin-bottom:8px; }
            .item { display:flex; justify-content:space-between; padding:7px 0; border-bottom:1px solid #f5ede0; font-size:12.5px; }
            .item:last-child { border-bottom:none; }
            .item-name { font-weight:500; }
            .item-unit { font-size:11px; color:#8b6347; margin-top:2px; }
            .totals { margin-top:12px; padding-top:12px; border-top:2px dashed #e2d5c8; }
            .t-row { display:flex; justify-content:space-between; font-size:12px; color:#5a3820; margin-bottom:7px; }
            .gst-row { display:flex; justify-content:space-between; font-size:13px; font-weight:700; color:#c96428; margin-bottom:7px; padding:6px 10px; background:#fff8f3; border-radius:8px; border:1px solid #f0d0b0; }
            .grand { display:flex; justify-content:space-between; font-family:'Playfair Display',serif; font-size:20px; font-weight:700; color:#c96428; margin-top:10px; padding-top:10px; border-top:2px solid #c96428; }
            .gstin { font-size:10px; color:#8b6347; margin-top:6px; font-family:monospace; }
            .footer { text-align:center; margin-top:22px; padding-top:18px; border-top:2px dashed #e2d5c8; }
            .thank { font-family:'Playfair Display',serif; font-size:17px; font-weight:700; color:#c96428; margin-bottom:8px; }
            .footer-msg { font-size:11px; color:#8b6347; line-height:1.7; }
            .footer-url { font-size:11px; color:#5a3820; font-weight:600; margin-top:8px; }
            .dots { letter-spacing:6px; color:#e2d5c8; font-size:12px; margin-top:14px; }
            @media print { body { padding:0; } }
          </style>
        </head>
        <body>
          <div class="receipt">
            <div class="center">
              <div class="logo">A</div>
              <div class="cafe-name">${settings.cafe_name || 'SwadZone'}</div>
              <div class="small">${settings.cafe_address || ''}<br/>${settings.cafe_phone || ''} | ${settings.cafe_email || ''}</div>
              ${settings.gst_number ? `<div class="small" style="margin-top:4px;font-weight:600;color:#5a3820;font-family:monospace">${settings.gst_number}</div>` : ''}
              <div class="tag">✦ Official Receipt ✦</div>
            </div>
            <hr/>
            <div class="info-box">
              <div class="row"><span class="lbl">Order ID</span><span class="val order-id">#${order.order_number || order.id}</span></div>
              <div class="row"><span class="lbl">Date & Time</span><span class="val">${formatDate(order.created_at)}</span></div>
              <div class="row"><span class="lbl">Customer</span><span class="val">${order.customer_name}</span></div>
              <div class="row"><span class="lbl">Phone</span><span class="val">${order.phone}</span></div>
              <div class="row"><span class="lbl">Order Type</span><span class="val">${(order.order_type || 'Dine-In').replace('-',' ').replace(/\b\w/g,l=>l.toUpperCase())}</span></div>
              ${order.order_type === 'delivery' && order.address ? `<div class="row"><span class="lbl">Address</span><span class="val">${order.address}</span></div>` : ''}
            </div>
            <div class="items-hdr"><span>Item</span><span>Amount</span></div>
            ${items.map(item => `
              <div class="item">
                <div>
                  <div class="item-name">${item.item_name || item.name}</div>
                  <div class="item-unit">₹${parseFloat(item.price).toFixed(0)} × ${item.quantity}</div>
                </div>
                <div style="font-weight:600">₹${parseFloat(item.subtotal || item.price * item.quantity).toFixed(0)}</div>
              </div>
            `).join('')}
            <div class="totals">
              <div class="t-row"><span>Items Total</span><span>₹${itemsTotal.toFixed(0)}</span></div>
              ${discountAmt > 0 ? `<div class="gst-row" style="background:#dcfce7;color:#16a34a;border-color:#a5d6a7"><span>🎟️ ${couponCode || 'Discount'}</span><span>- ₹${discountAmt.toFixed(0)}</span></div>` : ''}
              ${gstEnabled
                ? `<div class="gst-row"><span>${settings.gst_label || 'GST'} @ ${gstRate}%</span><span>+ ₹${gstAmount.toFixed(0)}</span></div>`
                : `<div class="t-row"><span>Tax</span><span>Nil</span></div>`
              }
              ${order?.order_type === 'delivery' ? `<div class="gst-row" style="background:#fff4ee;color:#c96428;border-color:#f0d0b0"><span>🛵 Delivery Charge</span><span>${deliveryFee > 0 ? `+ ₹${deliveryFee.toFixed(0)}` : 'FREE'}</span></div>` : ''}
              <div class="grand"><span>Total Payable</span><span>₹${actualTotal.toFixed(0)}</span></div>
              ${gstEnabled && settings.gst_number ? `<div class="gstin">${settings.gst_number} | ${settings.gst_label}: ₹${gstAmount.toFixed(2)}</div>` : ''}
            </div>
            <div class="footer">
              <div class="thank">Thank You! 🙏</div>
              <div class="footer-msg">We hope you enjoyed your experience at<br/><strong style="color:#1a0f00">${settings.cafe_name || 'SwadZone'}</strong>.<br/>We look forward to serving you again!</div>
              <div class="footer-url">www.aromacafe.in</div>
              <div class="dots">✦ ✦ ✦</div>
            </div>
          </div>
          <script>window.onload=function(){window.print();window.onafterprint=function(){window.close();};};</script>
        </body>
      </html>
    `;
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const newWindow = window.open(url, '_blank');
    if (newWindow) {
      newWindow.onload = () => { newWindow.print(); };
    } else {
      const a = document.createElement('a');
      a.href = url;
      a.download = `receipt-${order.order_number || order.id}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/70" onClick={onClose} />
      <motion.div initial={{ opacity: 0, scale: 0.92, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        className="relative rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
        style={{ background: '#ffffff', border: '1px solid #e2d5c8', maxHeight: '90vh', overflowY: 'auto' }}>

        <div className="flex items-center justify-between px-5 py-4 sticky top-0 z-10"
          style={{ background: '#ffffff', borderBottom: '1px solid #f0e8db' }}>
          <div>
            <h2 className="font-bold" style={{ color: '#1a0f00', fontSize: 16 }}>🧾 Receipt Preview</h2>
            <p style={{ color: '#8b6347', fontSize: 12 }}>Order #{order.order_number || order.id}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={handleDownloadPDF}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold text-white"
              style={{ background: 'linear-gradient(135deg,#c96428,#a84e1e)', boxShadow: '0 4px 12px rgba(201,100,40,0.35)' }}>
              📥 Download PDF
            </button>
            <button onClick={onClose} className="px-3 py-2 rounded-xl text-sm"
              style={{ background: '#f5ede0', color: '#8b6347' }}>✕</button>
          </div>
        </div>

        <div className="p-5" ref={receiptRef} style={{background:'#ffffff'}}>
          <div className="text-center mb-4 pb-4" style={{ borderBottom: '2px dashed rgba(255,255,255,0.08)' }}>
            {settings.navbar_logo_url ? (
              <img src={settings.navbar_logo_url} alt="logo" className="w-11 h-11 rounded-xl object-cover mx-auto mb-2" />
            ) : (
              <div className="w-11 h-11 rounded-xl flex items-center justify-center mx-auto mb-2 font-display font-bold text-xl text-white"
                style={{ background: '#c96428' }}>{(settings.cafe_name || 'A').charAt(0).toUpperCase()}</div>
            )}
            <p className="font-display font-bold" style={{ color: '#1a0f00', fontSize: 16 }}>{settings.cafe_name}</p>
            <p style={{ color: '#8b6347', fontSize: 11, marginTop: 2 }}>{settings.cafe_address}</p>
            {settings.gst_number && (
              <p style={{ color: '#5a3820', fontSize: 10, marginTop: 3, fontFamily: 'monospace' }}>{settings.gst_number}</p>
            )}
          </div>

          <div className="rounded-xl p-3 mb-4" style={{ background: '#fdf6f0', border: '1px solid #e8d5c0' }}>
            {[['Order ID', `#${order.order_number || order.id}`, true], ['Customer', order.customer_name, false],
              ['Phone', order.phone, false], ['Type', (order.order_type || 'Dine-In').replace('-',' '), false],
              ['Date', formatDate(order.created_at), false],
              ...(order.order_type === 'delivery' && order.address ? [['Address', order.address, false]] : [])
            ].map(([label, value, highlight]) => (
              <div key={label} className="flex justify-between mb-1.5 last:mb-0" style={{ fontSize: 12 }}>
                <span style={{ color: '#8b6347' }}>{label}</span>
                <span style={{ color: highlight ? '#c96428' : '#1a0f00', fontWeight: highlight ? 700 : 500 }}>{value}</span>
              </div>
            ))}
          </div>

          <div className="mb-3">
            <div className="flex justify-between pb-2 mb-2" style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#6b4226', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <span>Item</span><span>Amount</span>
            </div>
            {items.map((item, i) => (
              <div key={i} className="flex justify-between py-2" style={{ borderBottom: '1px solid #f0e6db', fontSize: 12 }}>
                <div>
                  <p style={{ color: '#1a0f00', fontWeight: 500 }}>{item.item_name || item.name}</p>
                  <p style={{ color: '#6b4226', fontSize: 11 }}>₹{parseFloat(item.price).toFixed(0)} × {item.quantity}</p>
                </div>
                <span style={{ color: '#c96428', fontWeight: 600 }}>₹{parseFloat(item.subtotal || item.price * item.quantity).toFixed(0)}</span>
              </div>
            ))}
          </div>

          <div className="pt-3" style={{ borderTop: '2px dashed #e2d5c8' }}>
            <div className="flex justify-between mb-2" style={{ fontSize: 12, color: '#5a3820' }}>
              <span>Subtotal</span><span>₹{subtotal.toFixed(0)}</span>
            </div>
            {gstEnabled ? (
              <div className="flex justify-between mb-2" style={{ fontSize: 12, color: '#5a3820' }}>
                <span>{settings.gst_label} @ {gstRate}%</span>
                <span>+ ₹{gstAmount.toFixed(0)}</span>
              </div>
            ) : (
              <div className="flex justify-between mb-2" style={{ fontSize: 12, color: '#4a2a10' }}>
                <span>Tax</span><span>Nil</span>
              </div>
            )}
            {order?.order_type === 'delivery' && (
              <div className="flex justify-between mb-2" style={{ fontSize: 12, color: '#c96428', fontWeight: 600 }}>
                <span>🛵 Delivery Charge</span>
                <span>{deliveryFee > 0 ? `+ ₹${deliveryFee.toFixed(0)}` : 'FREE'}</span>
              </div>
            )}
            <div className="flex justify-between mt-2 pt-2 font-bold"
              style={{ fontSize: 18, borderTop: '2px solid #c96428', fontFamily: 'Playfair Display, serif' }}>
              <span style={{ color: '#1a0f00' }}>Total Payable</span>
              <span style={{ color: '#c96428' }}>₹{grandTotal.toFixed(0)}</span>
            </div>
            {gstEnabled && settings.gst_number && (
              <p style={{ color: '#4a2a10', fontSize: 10, marginTop: 4, fontFamily: 'monospace' }}>
                {settings.gst_number}
              </p>
            )}
          </div>

          <div className="text-center mt-4 pt-4" style={{ borderTop: '2px dashed #e2d5c8' }}>
            <p className="font-display font-bold" style={{ color: '#c96428', fontSize: 15 }}>Thank You! 🙏</p>
            <p style={{ color: '#8b6347', fontSize: 11, marginTop: 4 }}>Visit us again at {settings.cafe_name}!</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ReceiptGenerator;
